// src/shared/api/ScriptInterpreterApi.ts
import yaml from 'js-yaml';
import { fetchData } from '../../utils/fetch';

// Base path for your script interpreter endpoints
const SCRIPT_INTERPRETER_PATH = '/script_interpreter';

export interface EnvVariables {
  [key: string]: string | number | boolean;
}

export interface StackScript {
  service: string;
  endpoint: string;
  payload: Record<string, any>;
}

export interface ScriptStackRequest {
  /** Optional identifiers for the script run */
  id_script?: number;
  script_name?: string;
  /** Environment variables available to all scripts */
  env_variables: EnvVariables;
  /** Sequence of service calls */
  stack_scripts: StackScript[];
}

/**
 * Serialize a script stack request to YAML, including optional identifiers.
 */
export function createYamlBody(
  envVariables: EnvVariables,
  stackScripts: StackScript[],
  idScript?: number,
  scriptName?: string,
): string {
  const request: ScriptStackRequest = {
    env_variables: envVariables,
    stack_scripts: stackScripts,
  };
  if (idScript !== undefined) {
    request.id_script = idScript;
  }
  if (scriptName) {
    request.script_name = scriptName;
  }

  return yaml.dump(request, { indent: 2, lineWidth: -1 });
}

/**
 * Execute a stack of scripts by POSTing YAML to the interpreter.
 * Uses fetchData to include auth headers automatically.
 */
export async function executeScriptStack(
  envVariables: EnvVariables,
  stackScripts: StackScript[],
  idScript?: number,
  scriptName?: string,
): Promise<string> {
  const yamlBody = createYamlBody(envVariables, stackScripts, idScript, scriptName);
  // POST to /script_interpreter/execute_script_stack via fetchData
  const raw = await fetchData(`${SCRIPT_INTERPRETER_PATH}/execute_script_stack`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-yaml' },
    body: yamlBody,
  });
  // fetchData may return string or parsed JSON
  if (typeof raw === 'string') {
    return raw;
  }
  return JSON.stringify(raw);
}
